import { Badge, Text, rem } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';

import {
  BpEventParams,
  defaultTemplateProps,
  EnterpriseDefaultFeatures,
  PlanQuotas,
} from './interface';
import translations from './translations';

// Explicitly using an enum here for the convenience of being able to use this as
// a type and as specific values.
// eslint-disable-next-line typescript-enum/no-enum
export enum PLANS {
  BASIC = 'basic',
  CUSTOM_SELF_SERVE = 'customSelfServe',
  PLUS = 'plus',
  PRO = 'pro',
  PRO_U = 'proUnlimited',
  BUSINESS = 'business',
  PREMIUM = 'livePremium',
  ENTERPRISE = 'enterprise',
  OTT = 'ott',
  FREE = 'free',
  STARTER = 'starter',
  STANDARD = 'standard',
  ADVANCED = 'advanced',
  PRO_SOLUTION = 'proSolution',
  TEAM = 'team',
  TEAM_LIVE = 'teamLive',
}

export type PlansArray = Array<PLANS>;

export const DEFAULT_SEAT_PLANS = [
  PLANS.FREE,
  PLANS.STARTER,
  PLANS.STANDARD,
  PLANS.ADVANCED,
  PLANS.ENTERPRISE,
];

export const SOLUTION_PLANS = [
  PLANS.FREE,
  PLANS.PRO_SOLUTION,
  PLANS.TEAM,
  PLANS.TEAM_LIVE,
  PLANS.ENTERPRISE,
];

export const LEGACY_PLANS = [
  PLANS.BASIC,
  PLANS.PLUS,
  PLANS.PRO,
  PLANS.BUSINESS,
  PLANS.PREMIUM,
  PLANS.ENTERPRISE,
];

export const SEAT_TIER_PLANS = [...DEFAULT_SEAT_PLANS, ...SOLUTION_PLANS];

export const PLANS_ORDER = {
  [PLANS.BASIC]: 0,
  [PLANS.PLUS]: 1,
  [PLANS.PRO]: 2,
  [PLANS.BUSINESS]: 3,
  [PLANS.PREMIUM]: 4,
  [PLANS.FREE]: 0,
  [PLANS.STARTER]: 1,
  [PLANS.STANDARD]: 2,
  [PLANS.ADVANCED]: 3,
  [PLANS.PRO_SOLUTION]: 1,
  [PLANS.TEAM]: 2,
  [PLANS.TEAM_LIVE]: 3,
  [PLANS.ENTERPRISE]: 4,
};

export const UNSUPPORTED_PLANS = [PLANS.FREE, PLANS.BASIC, PLANS.PRO_U, PLANS.OTT];

export const PLANS_DISPLAY_NAMES = {
  [PLANS.BASIC]: 'Basic',
  [PLANS.PLUS]: 'Plus',
  [PLANS.PRO]: 'Pro',
  [PLANS.BUSINESS]: 'Business',
  [PLANS.PREMIUM]: 'Premium',
  [PLANS.FREE]: 'Free',
  [PLANS.STARTER]: 'Starter',
  [PLANS.STANDARD]: 'Standard',
  [PLANS.ADVANCED]: 'Advanced',
  [PLANS.PRO_SOLUTION]: 'Pro',
  [PLANS.TEAM]: 'Team',
  [PLANS.TEAM_LIVE]: 'Team Live',
  [PLANS.ENTERPRISE]: 'Enterprise',
};

// Hardcoding the storage value of Plus & Pro plan since the data is not avaialble via API. Considering
// the upcoming migration of legacy plan users to new plan, this harcoded data will become redundant
// in future.
export const PLANS_SUBHEADING = {
  [PLANS.PLUS]: translate('Grow your business with essential video tools'),
  [PLANS.PRO]: translate('Boost your impact with premium creation'),
  [PLANS.BUSINESS]: translate('Capture leads with engagement tools'),
  [PLANS.PREMIUM]: translate('Drive engagement with Vimeo AI and events'),
  [PLANS.STARTER]: translate('Grow your business with essential video tools'),
  [PLANS.STANDARD]: translate('Boost your impact with premium creation'),
  [PLANS.ADVANCED]: translate('Drive engagement with Vimeo AI and events'),
  [PLANS.PRO_SOLUTION]: null,
  [PLANS.TEAM]: null,
  [PLANS.TEAM_LIVE]: null,
  [PLANS.ENTERPRISE]: translate('More security, control, and support'),
};

export const PLANS_DEFAULT_FEATURES = {
  [PLANS.PLUS]: [
    translate('Privacy controls'),
    translate('Custom video player'),
    translate({
      singular: 'Subtitle translations (with AI credits) {NEW_BADGE}',
      replacements: {
        NEW_BADGE: () => (
          <Badge size="sm" variant="new">
            {translate('New')}
          </Badge>
        ),
      },
    }),
  ],
  [PLANS.PRO]: [
    translate('Text-based video editing'),
    translate('Engagement and social analytics'),
  ],
  [PLANS.PREMIUM]: [
    translate('AI-generated chapters and text summaries'),
    translate('Enable creators to ask questions on videos'),
    translate('Host live broadcasts and webinars'),
  ],
  [PLANS.BUSINESS]: [
    translate('Brand kit'),
    translate('Showcase SEO'),
    translate('Lead generation'),
    translate('End Cards'),
    translate('Custom branding'),
  ],
  [PLANS.STARTER]: [
    translate('Customizable video player'),
    translate('Password privacy & unlisted links'),
    translate({
      singular: 'Review & collaboration tools {NEW_BADGE}',
      replacements: {
        NEW_BADGE: (
          <Badge size="xs" variant="new" position="absolute" marginLeft={rem(5)}>
            {translate('New')}
          </Badge>
        ),
      },
    }),
  ],
  [PLANS.STANDARD]: [
    translate('Branding in the player'),
    translate('Third party player support'),
    translate('Custom watermark'),
    translate('Lead capture'),
  ],
  [PLANS.ADVANCED]: [
    translate('Host livestreamed events'),
    translate('Events Q&A, polls, and chat'),
    translate('Stream to multiple destinations'),
    translate('Marketing automation integrations'),
  ],
  [PLANS.PRO_SOLUTION]: [
    translate('Customizable video player'),
    translate('Password protection'),
    translate('Review tools'),
    translate({
      singular: 'Subtitle translations (with AI credits) {NEW_BADGE}',
      replacements: {
        NEW_BADGE: () => (
          <Badge size="sm" variant="new">
            {translate('New')}
          </Badge>
        ),
      },
    }),
  ],
  [PLANS.TEAM]: [
    translate('Custom logo in the player'),
    translate('Customizable showcases'),
    translate('Custom forms'),
  ],
  [PLANS.TEAM_LIVE]: [
    translate('Host livestreamed events'),
    translate('Event Q&A, polls, and chat'),
    translate('DVR streaming'),
  ],
  [PLANS.ENTERPRISE]: [
    translate('Custom permissions'),
    translate('Advanced AI capabilities'),
    translate('SSO (SAML) and SCIM (OKTA, AZURE)'),
    translate('Quality events (backup streams, eCDN)'),
    translate('Dedicated support'),
  ],
};

export const defaultBpEventParams: BpEventParams = {
  copy: null,
  location: null,
  page: null,
  target: null,
  plan_selected: null,
  purchase_type: null,
  duration: null,
  price: null,
  currency: null,
  is_discount: null,
  discount_offer: null,
  path: null,
  target_path: null,
  device_type: null,
  upsell_name: null,
  feature: null,
  is_new_pricing: null,
  upgrade_flags: null,
  loading_time: null,
  promo_code_id: null,
  third_party_integration: null,
};

export const ADVANCED_ENTERPRISE_UPSELL_FORM_URL: string =
  '/enterprise-contact-us-upsell-modal?modal=advanced_enterprise_upsell';

export const CLOSE_EVENT_DATA: string = 'triggerCloseButtonClick';

export const enterpriseDefaultFeatures: EnterpriseDefaultFeatures = {
  title: translations.doMoreWithEnterprise,
  subTitle: translations.productBenefits,
  featuresList: [
    translations.unlimitedVideos,
    translations.moreTeamMembers,
    translations.authenticationProtocols,
    translations.advancedMarketingIntegrations,
    translations.backupStreams,
  ],
};

const getSeatCountCopy = (
  seats: number | null | undefined,
  defaultValue: number,
  showUserCopy: boolean | undefined,
) => {
  const seatCount = seats ? seats : defaultValue;

  if (showUserCopy)
    return translate({
      singular: '{TOTAL_USERS} user',
      plural: '{TOTAL_USERS} users',
      replacements: { TOTAL_USERS: seatCount },
      count: seatCount,
    });

  return translate({
    singular: '{TOTAL_USERS} seat',
    plural: '{TOTAL_USERS} seats',
    replacements: { TOTAL_USERS: seatCount },
    count: seatCount,
  });
};

export const PLANS_DEFAULT_VIDEO_QUOTAS: PlanQuotas = {
  [PLANS.STARTER]: {
    getSeatCount: (teamSeats, showUserCopy) => getSeatCountCopy(teamSeats, 1, showUserCopy),
    hasToopTip: true,
    getVideoCount: (videoCount) =>
      translate({
        singular: '{NUM_VIDEOS} videos',
        replacements: { NUM_VIDEOS: videoCount || '60' },
      }),
    getStorageQuota: (storageQuota) =>
      translate({
        singular: '{TOTAL_STORAGE} storage',
        replacements: { TOTAL_STORAGE: storageQuota || '100 GB' },
      }),
    getBandwidth: (bandwidth) =>
      translate({
        singular: '{BANDWIDTH} annual bandwidth',
        replacements: { BANDWIDTH: bandwidth || '2 TB' },
      }),
  },
  [PLANS.STANDARD]: {
    getSeatCount: (teamSeats, showUserCopy) => getSeatCountCopy(teamSeats, 1, showUserCopy),
    hasToopTip: true,
    getVideoCount: (videoCount) =>
      translate({
        singular: '{NUM_VIDEOS} videos',
        replacements: { NUM_VIDEOS: videoCount || '120' },
      }),
    getStorageQuota: (storageQuota) =>
      translate({
        singular: '{TOTAL_STORAGE} storage',
        replacements: { TOTAL_STORAGE: storageQuota || '1 TB' },
      }),
    getBandwidth: (bandwidth) =>
      translate({
        singular: '{BANDWIDTH} annual bandwidth',
        replacements: { BANDWIDTH: bandwidth || '2 TB' },
      }),
  },
  [PLANS.ADVANCED]: {
    getSeatCount: (teamSeats, showUserCopy) => getSeatCountCopy(teamSeats, 1, showUserCopy),
    hasToopTip: true,
    getVideoCount: (videoCount) =>
      translate({
        singular: '{NUM_VIDEOS} videos',
        replacements: { NUM_VIDEOS: videoCount || '500' },
      }),
    getStorageQuota: (storageQuota) =>
      translate({
        singular: '{TOTAL_STORAGE} storage',
        replacements: { TOTAL_STORAGE: storageQuota || '5 TB' },
      }),
    getBandwidth: (bandwidth) =>
      translate({
        singular: '{BANDWIDTH} annual bandwidth',
        replacements: { BANDWIDTH: bandwidth || '2 TB' },
      }),
  },
  [PLANS.PRO_SOLUTION]: {
    getSeatCount: (teamSeats, showUserCopy) => getSeatCountCopy(teamSeats, 2, showUserCopy),
    hasToopTip: false,
    getVideoCount: (videoCount) =>
      translate({
        singular: '{NUM_VIDEOS} videos',
        replacements: { NUM_VIDEOS: videoCount || '60' },
      }),
    getStorageQuota: (storageQuota) =>
      translate({
        singular: '{TOTAL_STORAGE} storage',
        replacements: { TOTAL_STORAGE: storageQuota || '250 GB' },
      }),
    getBandwidth: (bandwidth) =>
      translate({
        singular: '{BANDWIDTH} bandwidth per year',
        replacements: { BANDWIDTH: bandwidth || '5 TB' },
      }),
  },
  [PLANS.TEAM]: {
    getSeatCount: (teamSeats, showUserCopy) => getSeatCountCopy(teamSeats, 5, showUserCopy),
    hasToopTip: false,
    getVideoCount: (videoCount) =>
      translate({
        singular: '{NUM_VIDEOS} videos',
        replacements: { NUM_VIDEOS: videoCount || '120' },
      }),
    getStorageQuota: (storageQuota) =>
      translate({
        singular: '{TOTAL_STORAGE} storage',
        replacements: { TOTAL_STORAGE: storageQuota || '2 TB' },
      }),
    getBandwidth: (bandwidth) =>
      translate({
        singular: '{BANDWIDTH} bandwidth per year',
        replacements: { BANDWIDTH: bandwidth || '8 TB' },
      }),
  },
  [PLANS.TEAM_LIVE]: {
    getSeatCount: (teamSeats, showUserCopy) => getSeatCountCopy(teamSeats, 10, showUserCopy),
    hasToopTip: false,
    getVideoCount: (videoCount) =>
      translate({
        singular: '{NUM_VIDEOS} videos',
        replacements: { NUM_VIDEOS: videoCount || '500' },
      }),
    getStorageQuota: (storageQuota) =>
      translate({
        singular: '{TOTAL_STORAGE} storage',
        replacements: { TOTAL_STORAGE: storageQuota || '7 TB' },
      }),
    getBandwidth: (bandwidth) =>
      translate({
        singular: '{BANDWIDTH} bandwidth per year',
        replacements: { BANDWIDTH: bandwidth || '12 TB' },
      }),
  },
  [PLANS.ENTERPRISE]: {
    getSeatCount: (teamSeats, showUserCopy) =>
      showUserCopy ? translate('More users') : translate('More seats'),
    hasToopTip: false,
    getVideoCount: () => translate('More Storage & Bandwidth'),
    getStorageQuota: () => translate('Custom storage'),
    getBandwidth: () => translate('More Bandwidth'),
  },
};

const BoldText = (children: string) => (
  <Text as="span" variant="heading-sm">
    {children}
  </Text>
);

export const includedInAllPlansModalConfig: defaultTemplateProps = {
  hidePlansSummary: true,
  hasFreeFeaturedBanner: true,
  headerText: '',
  subHeaderText: '',
};

export const quotaModalConfig: defaultTemplateProps = {
  headerText: translate('Upgrade to unlock more storage'),
};

export const quotaModalAtLimitConfig: defaultTemplateProps = {
  headerText: translate("You've reached your storage limit"),
};

export const teamsModalConfig: defaultTemplateProps = {
  headerText: translate('Upgrade to start working together'),
};

export const standardAndAboveModalConfig: defaultTemplateProps = {
  ...includedInAllPlansModalConfig,
  featuredBannerText: translate({
    singular: 'This feature is included in {BOLD}Standard and above{/BOLD}',
    replacements: {
      BOLD: BoldText,
    },
  }),
};

export const advancedAndEnterpriseModalConfig: defaultTemplateProps = {
  ...includedInAllPlansModalConfig,
  recommendedPlan: PLANS.ADVANCED,
  featuredBannerText: translate({
    singular: 'This feature is included in {BOLD}Advanced and Enterprise{/BOLD}',
    replacements: {
      BOLD: BoldText,
    },
  }),
};
